/**
 * Журнал вызовов ИИ: все платные обращения к провайдерам с ценой, статусом и ссылкой на результат.
 *
 * Фильтры живут в URL (ссылкой можно поделиться), страницы — по 200 строк со сдвигом `offset`.
 * Сводки «по сотрудникам» и «по моделям» считает сервис по ВСЕМ строкам фильтра, а не по странице.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";

import { Btn, Empty, ErrorLine, Hint, inputClass, Panel, selectClass } from "@/components/project/ui";
import { Loading, Segmented } from "@/components/trends/ui";
import { RetryImg } from "@/components/trends/PinGrid";
import { callLink, callsKeys, KIND_LABEL, loadCalls, STATUS_LABEL, type CallRow, type CallsQuery } from "@/lib/calls";
import { providerMark } from "@/lib/callProvider";
import { mediaUrl, when } from "@/lib/media";
import { errorText, usd } from "@/lib/project";
import { retryQuery } from "@/lib/trends";

const PAGE = 200;

const statusTone = (s: string): string =>
  s === "ok" ? "text-ok" : s === "running" ? "text-warn" : "text-err";

/** Параметры вызова одной строкой: «3:4 · 1K · 2 · 1 вх.» — что есть, то и показываем. */
function paramsText(p: Record<string, unknown>): string {
  const parts: string[] = [];
  if (p.aspect_ratio !== undefined) parts.push(String(p.aspect_ratio));
  const size = p.resolution ?? p.quality;
  if (size !== undefined) parts.push(String(size));
  if (p.n !== undefined) parts.push(String(p.n));
  if (p.inputs !== undefined) parts.push(`${String(p.inputs)} вх.`);
  return parts.join(" · ");
}

const shortModel = (m: string): string => m.split("/").pop() ?? m;

function Row({ c }: { c: CallRow }) {
  const link = callLink(c);
  return (
    <tr className="border-t border-border align-middle">
      <td className="px-2 py-1.5 whitespace-nowrap text-fg-muted tnum">{when(c.date_create)}</td>
      <td className="px-2 py-1.5">{c.author_name}</td>
      <td className="px-2 py-1.5">{KIND_LABEL[c.kind] ?? c.kind}</td>
      <td className="px-2 py-1.5" title={c.model}>
        {shortModel(c.model)}
        {providerMark(c.provider)}
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap text-fg-muted">{paramsText(c.params)}</td>
      <td className={`px-2 py-1.5 ${statusTone(c.status)}`} title={c.error ?? undefined}>
        {STATUS_LABEL[c.status] ?? c.status}
      </td>
      <td className="px-2 py-1.5 text-fg-muted tnum">{c.http_status ?? "—"}</td>
      <td className="px-2 py-1.5 text-right tnum">{usd(c.cost, 4)}</td>
      <td className="px-2 py-1.5 text-right text-fg-muted tnum">{c.seconds !== null ? c.seconds.toFixed(1) : "—"}</td>
      <td className="px-2 py-1.5">
        <div className="flex gap-1">
          {c.assets.slice(0, 3).map((a) => (
            <div key={a} className="size-8 flex-none overflow-hidden rounded-[var(--radius-ui)] bg-bg-hover">
              <RetryImg src={mediaUrl(a)} alt="" className="size-8 object-cover" />
            </div>
          ))}
        </div>
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        {link ? (
          <Link to={link} className="text-accent-text hover:underline">
            открыть результат
          </Link>
        ) : c.link.reference_deleted ? (
          <span className="text-fg-muted">
            референс удалён (был: {c.link.reference_deleted.library_name ?? "библиотека"} #{c.link.reference_deleted.id ?? "?"})
          </span>
        ) : null}
      </td>
    </tr>
  );
}

export function CallsPage() {
  const [params, setParams] = useSearchParams();
  const offset = Number(params.get("offset")) || 0;
  const q: CallsQuery = {
    ...(params.get("user_id") ? { user_id: params.get("user_id") as string } : {}),
    ...(params.get("model") ? { model: params.get("model") as string } : {}),
    ...(params.get("kind") ? { kind: params.get("kind") as string } : {}),
    ...(params.get("from") ? { from: params.get("from") as string } : {}),
    ...(params.get("to") ? { to: params.get("to") as string } : {}),
    ...(offset > 0 ? { offset } : {}),
  };
  const { data, error, isPending } = useQuery({
    queryKey: callsKeys.list(q),
    queryFn: () => loadCalls(q),
    retry: retryQuery,
  });

  // Смена фильтра возвращает на первую страницу: сдвиг от прежней выборки ничего не значит.
  const set = (name: string, value: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(name, value);
      else next.delete(name);
      next.delete("offset");
      return next;
    });
  const page = (to: number) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (to > 0) next.set("offset", String(to));
      else next.delete("offset");
      return next;
    });

  const kinds = [
    { value: "", label: "все" },
    ...Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label })),
  ];

  return (
    <div className="grid gap-4 p-5">
      <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-fg">Журнал вызовов</h1>

      <div className="flex flex-wrap items-center gap-2" data-annot="фильтры журнала">
        <select
          className={selectClass}
          value={params.get("user_id") ?? ""}
          onChange={(e) => set("user_id", e.target.value)}
          aria-label="сотрудник"
        >
          <option value="">все сотрудники</option>
          {(data?.users ?? []).map((u) => (
            <option key={u.user_id} value={u.user_id}>
              {u.name ?? `#${u.user_id}`}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={params.get("model") ?? ""}
          onChange={(e) => set("model", e.target.value)}
          aria-label="модель"
        >
          <option value="">все модели</option>
          {(data?.models ?? []).map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <Segmented label="вид" options={kinds} value={params.get("kind") ?? ""} onPick={(v) => set("kind", v)} />
        <label className="flex items-center gap-1.5 text-[12px] text-fg-faint">
          с
          <input
            type="date"
            className={inputClass}
            value={params.get("from") ?? ""}
            onChange={(e) => set("from", e.target.value)}
          />
        </label>
        <label className="flex items-center gap-1.5 text-[12px] text-fg-faint">
          по
          <input
            type="date"
            className={inputClass}
            value={params.get("to") ?? ""}
            onChange={(e) => set("to", e.target.value)}
          />
        </label>
      </div>

      {isPending ? <Loading /> : null}
      <ErrorLine text={error ? errorText(error) : null} />

      {data ? (
        <>
          <div className="text-[13px] text-fg tnum">
            {data.totals.count} вызовов · {usd(data.totals.cost, 2)}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Panel title="по сотрудникам" annot="сводка по сотрудникам">
              <div className="grid gap-1 text-[12.5px]">
                {data.totals.by_user.map((u) => (
                  <div key={u.user_id} className="flex gap-2 tnum">
                    <span className="min-w-0 flex-1 truncate">{u.name}</span>
                    <span className="text-fg-muted">{u.count}</span>
                    <span className="w-20 text-right">{usd(u.cost, 2)}</span>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel title="по моделям" annot="сводка по моделям">
              <div className="grid gap-1 text-[12.5px]">
                {data.totals.by_model.map((m) => (
                  <div key={m.model} className="flex gap-2 tnum">
                    <span className="min-w-0 flex-1 truncate" title={m.model}>
                      {shortModel(m.model)}
                    </span>
                    <span className="text-fg-muted">{m.count}</span>
                    <span className="w-20 text-right">{usd(m.cost, 2)}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          {data.calls.length === 0 ? (
            <Empty>Вызовов по этому фильтру нет.</Empty>
          ) : (
            <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border bg-bg">
              <table className="w-full text-left text-[12.5px]">
                <thead className="text-[11.5px] text-fg-faint">
                  <tr>
                    {["время", "кто", "вид", "модель", "параметры", "статус", "HTTP", "$", "сек", "результат", ""].map(
                      (h, i) => (
                        <th key={i} className="px-2 py-1.5 font-normal">
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.calls.map((c) => (
                    <Row key={c.id} c={c} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Btn disabled={offset <= 0} onClick={() => page(Math.max(0, offset - PAGE))}>
              ← назад
            </Btn>
            <Hint>
              {data.total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE, data.total)} из {data.total}
            </Hint>
            <Btn disabled={offset + PAGE >= data.total} onClick={() => page(offset + PAGE)}>
              дальше →
            </Btn>
          </div>
        </>
      ) : null}
    </div>
  );
}
